export const BLOCK_SOURCE = 'agenda_time_block_dialog';
export const DEFAULT_BLOCK_MAX_DAYS = 60;
export const DEFAULT_HOUR_RANGE = Object.freeze({
  startTime: '08:00',
  endTime: '20:00',
});

const clockFromMinutes = value =>
  `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(
    value % 60
  ).padStart(2, '0')}`;
const dateValue = value => String(value || '').slice(0, 10);
const blockValue = (block, camelKey, snakeKey) =>
  block?.[camelKey] ?? block?.[snakeKey];
const minutesFromClockValue = value => {
  const [hours, minutes] = String(value || '')
    .split(':')
    .map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

export const addDaysIso = (baseIso, days) => {
  const [year, month, day] = String(baseIso || '')
    .split('-')
    .map(Number);
  if (!year || !month || !day) return '';

  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

export const buildHourSlots = (hourRange = DEFAULT_HOUR_RANGE) => {
  const start = minutesFromClockValue(
    hourRange.startTime || hourRange.start_time || DEFAULT_HOUR_RANGE.startTime
  );
  const end = minutesFromClockValue(
    hourRange.endTime || hourRange.end_time || DEFAULT_HOUR_RANGE.endTime
  );
  const startMinutes = Number.isFinite(start)
    ? Math.ceil(start / 60) * 60
    : 480;
  const endMinutes =
    Number.isFinite(end) && end > start ? Math.floor(end / 60) * 60 : 1200;
  const slots = [];

  for (let cursor = startMinutes; cursor < endMinutes; cursor += 60) {
    const startTime = clockFromMinutes(cursor);
    const endTime = clockFromMinutes(cursor + 60);
    slots.push({
      id: `${startTime}-${endTime}`,
      startTime,
      endTime,
      label: `${startTime} - ${endTime}`,
    });
  }

  return slots;
};

export const HOUR_SLOTS = buildHourSlots();

export const schedulePresetsForSlots = (hourSlots = HOUR_SLOTS) => {
  const idsBetween = (startTime, endTime) =>
    hourSlots
      .filter(
        slot =>
          minutesFromClockValue(slot.startTime) >=
            minutesFromClockValue(startTime) &&
          minutesFromClockValue(slot.endTime) <= minutesFromClockValue(endTime)
      )
      .map(slot => slot.id);
  const descriptionForIds = (ids, fallback) => {
    const selected = hourSlots.filter(slot => ids.includes(slot.id));
    if (!selected.length) return fallback;

    return `${selected[0].startTime} - ${selected[selected.length - 1].endTime}`;
  };
  const allDayIds = hourSlots.map(slot => slot.id);
  const morningIds = idsBetween('08:00', '12:00');
  const afternoonIds = idsBetween('13:00', '20:00');

  return {
    all_day: {
      label: 'Dia inteiro',
      description: descriptionForIds(allDayIds, '08:00 - 20:00'),
      slotIds: allDayIds,
    },
    morning: {
      label: 'Manhã',
      description: descriptionForIds(morningIds, '08:00 - 12:00'),
      slotIds: morningIds,
    },
    afternoon: {
      label: 'Tarde',
      description: descriptionForIds(afternoonIds, '13:00 - 20:00'),
      slotIds: afternoonIds,
    },
    custom: {
      label: 'Personalizado',
      description: 'Escolha os horários',
      slotIds: [],
    },
  };
};

export const SCHEDULE_PRESETS = schedulePresetsForSlots(HOUR_SLOTS);

export const slotIdsForSchedule = (
  scheduleType,
  selectedSlotIds = [],
  hourSlots = HOUR_SLOTS
) => {
  if (scheduleType === 'custom') return selectedSlotIds;
  return schedulePresetsForSlots(hourSlots)[scheduleType]?.slotIds || [];
};

export const contiguousRangesFromSlots = (slotIds, hourSlots = HOUR_SLOTS) => {
  const selected = hourSlots.filter(slot => slotIds.includes(slot.id));
  if (!selected.length) return [];

  const ranges = [];
  selected.forEach(slot => {
    const current = ranges[ranges.length - 1];
    if (current && current.endTime === slot.startTime) {
      current.endTime = slot.endTime;
      current.slotIds.push(slot.id);
      return;
    }

    ranges.push({
      startTime: slot.startTime,
      endTime: slot.endTime,
      slotIds: [slot.id],
    });
  });
  return ranges;
};

export const validateBlockDraft = (draft, defaultUserId, options = {}) => {
  const errors = [];
  const userId = draft.userId || defaultUserId;
  const selectedSlotIds = slotIdsForSchedule(
    draft.scheduleType,
    draft.selectedSlotIds,
    options.hourSlots
  );
  const minDate = options.minDate;
  const maxDate = options.maxDate;
  const allowRecurrent = options.allowRecurrent !== false;

  if (!userId) errors.push('Corretor obrigatório.');
  if (draft.blockType === 'recurrent' && !allowRecurrent) {
    errors.push('Bloqueios recorrentes devem ser solicitados ao suporte.');
  }
  if (draft.blockType === 'recurrent' && draft.weekday === '') {
    errors.push('Dia da semana obrigatório.');
  }
  if (draft.blockType !== 'recurrent') {
    if (!draft.dateRange?.start) errors.push('Data inicial obrigatória.');
    if (draft.dayMode === 'multiple' && !draft.dateRange?.end) {
      errors.push('Data final obrigatória.');
    }
    if (
      draft.dayMode === 'multiple' &&
      draft.dateRange?.start &&
      draft.dateRange?.end &&
      draft.dateRange.start > draft.dateRange.end
    ) {
      errors.push('A data final não pode ser anterior à inicial.');
    }
    if (
      draft.dayMode === 'multiple' &&
      draft.dateRange?.start &&
      draft.dateRange?.end &&
      draft.dateRange.start === draft.dateRange.end
    ) {
      errors.push('A data final deve ser posterior à inicial.');
    }
    if (minDate && draft.dateRange?.start && draft.dateRange.start < minDate) {
      errors.push('A data inicial não pode estar no passado.');
    }
    if (
      maxDate &&
      (draft.dateRange?.start > maxDate ||
        (draft.dateRange?.end && draft.dateRange.end > maxDate))
    ) {
      errors.push(
        `A data final deve estar dentro dos próximos ${
          options.maxDays || DEFAULT_BLOCK_MAX_DAYS
        } dias.`
      );
    }
  }
  if (!selectedSlotIds.length) errors.push('Selecione ao menos um horário.');

  return errors;
};

export const buildBlockPayloads = (draft, defaultUserId, options = {}) => {
  const userId = draft.userId || defaultUserId;
  const selectedSlotIds = slotIdsForSchedule(
    draft.scheduleType,
    draft.selectedSlotIds,
    options.hourSlots
  );
  const startsOn = dateValue(draft.dateRange?.start);
  const endsOn = dateValue(
    draft.dayMode === 'multiple' ? draft.dateRange?.end : draft.dateRange?.start
  );

  return contiguousRangesFromSlots(selectedSlotIds, options.hourSlots).map(
    range => {
      const payload = {
        user_id: userId,
        block_type: draft.blockType,
        duration_type: 'custom',
        start_time: range.startTime,
        end_time: range.endTime,
        reason: String(draft.reason || '').trim(),
        metadata: {
          source: BLOCK_SOURCE,
          schedule_type: draft.scheduleType,
          day_mode: draft.dayMode,
          slot_ids: range.slotIds,
        },
      };

      if (draft.blockType === 'recurrent') {
        payload.weekday = Number(draft.weekday);
      } else {
        payload.starts_on = startsOn;
        payload.ends_on = endsOn || startsOn;
      }

      return payload;
    }
  );
};

export const buildBulkBlockPayload = (draft, defaultUserId, options = {}) => {
  const hourSlots = options.hourSlots || HOUR_SLOTS;
  const userId = draft.userId || defaultUserId;
  const selectedSlotIds = slotIdsForSchedule(
    draft.scheduleType,
    draft.selectedSlotIds,
    hourSlots
  );
  const selected = new Set(selectedSlotIds);
  const startsOn = dateValue(draft.dateRange?.start);
  const endsOn = dateValue(
    draft.dayMode === 'multiple' ? draft.dateRange?.end : draft.dateRange?.start
  );

  const payload = {
    user_id: userId,
    block_type: draft.blockType,
    duration_type: 'custom',
    reason: String(draft.reason || '').trim(),
    is_entire_day: draft.scheduleType === 'all_day',
    schedule_type: draft.scheduleType,
    day_mode: draft.dayMode,
    hours: hourSlots.map(slot => ({
      id: slot.id,
      start_time: slot.startTime,
      end_time: slot.endTime,
      is_blocked: selected.has(slot.id),
    })),
    metadata: {
      source: BLOCK_SOURCE,
      schedule_type: draft.scheduleType,
      day_mode: draft.dayMode,
      slot_ids: selectedSlotIds,
    },
  };

  if (draft.blockType === 'recurrent') {
    payload.weekday = Number(draft.weekday);
  } else {
    payload.starts_on = startsOn;
    payload.ends_on = endsOn || startsOn;
  }

  return payload;
};

export const minutesFromClock = value => {
  const [hours, minutes] = String(value || '')
    .split(':')
    .map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
  return hours * 60 + minutes;
};

export const timeWindowsOverlap = (aStart, aEnd, bStart, bEnd) =>
  minutesFromClock(aStart) < minutesFromClock(bEnd) &&
  minutesFromClock(bStart) < minutesFromClock(aEnd);

export const weekdayLabel = value =>
  ({
    0: 'Domingo',
    1: 'Segunda-feira',
    2: 'Terça-feira',
    3: 'Quarta-feira',
    4: 'Quinta-feira',
    5: 'Sexta-feira',
    6: 'Sábado',
  })[Number(value)] || 'Dia recorrente';

const blockUserId = block => blockValue(block, 'userId', 'user_id');
const blockType = block => blockValue(block, 'blockType', 'block_type');
const blockStart = block => blockValue(block, 'startTime', 'start_time');
const blockEnd = block => blockValue(block, 'endTime', 'end_time');
const blockStartsOn = block =>
  dateValue(blockValue(block, 'startsOn', 'starts_on'));
const blockEndsOn = block =>
  dateValue(blockValue(block, 'endsOn', 'ends_on') || blockStartsOn(block));
const blockWeekday = block => blockValue(block, 'weekday', 'weekday');

const dateRangeOverlaps = (startA, endA, startB, endB) =>
  !!startA && !!startB && startA <= endB && startB <= endA;

const recurrentTouchesTemporary = (weekday, startsOn, endsOn) => {
  if (weekday === '' || weekday == null || !startsOn || !endsOn) return false;

  const current = new Date(`${startsOn}T00:00:00Z`);
  const end = new Date(`${endsOn}T00:00:00Z`);
  if (Number.isNaN(current.getTime()) || Number.isNaN(end.getTime())) {
    return false;
  }

  while (current <= end) {
    if (current.getUTCDay() === Number(weekday)) return true;
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return false;
};

export const blockDateTouchesPayload = (block, payload) => {
  const existingType = blockType(block);
  if (existingType === 'recurrent' && payload.block_type === 'recurrent') {
    return Number(blockWeekday(block)) === Number(payload.weekday);
  }
  if (existingType === 'recurrent') {
    return recurrentTouchesTemporary(
      blockWeekday(block),
      payload.starts_on,
      payload.ends_on
    );
  }
  if (payload.block_type === 'recurrent') {
    return recurrentTouchesTemporary(
      payload.weekday,
      blockStartsOn(block),
      blockEndsOn(block)
    );
  }

  return dateRangeOverlaps(
    blockStartsOn(block),
    blockEndsOn(block),
    payload.starts_on,
    payload.ends_on
  );
};

export const blockOverlapsPayload = (block, payload) =>
  String(blockUserId(block)) === String(payload.user_id) &&
  blockDateTouchesPayload(block, payload) &&
  timeWindowsOverlap(
    blockStart(block),
    blockEnd(block),
    payload.start_time,
    payload.end_time
  );

export const sameDayTemporaryBlocks = (blocks, payloads) => {
  const matches = new Map();
  blocks.forEach(block => {
    if (blockType(block) !== 'temporary') return;
    const touches = payloads.some(
      payload =>
        payload.block_type === 'temporary' &&
        String(blockUserId(block)) === String(payload.user_id) &&
        dateRangeOverlaps(
          blockStartsOn(block),
          blockEndsOn(block),
          payload.starts_on,
          payload.ends_on
        )
    );
    if (touches) matches.set(block.id, block);
  });
  return Array.from(matches.values());
};

export const overlappingBlocks = (blocks, payloads, ignoredIds = []) => {
  const ignored = new Set(ignoredIds.map(String));
  const matches = new Map();
  blocks.forEach(block => {
    if (ignored.has(String(block.id))) return;
    if (payloads.some(payload => blockOverlapsPayload(block, payload))) {
      matches.set(block.id, block);
    }
  });
  return Array.from(matches.values());
};

export const describePayloads = payloads => {
  if (!payloads.length) return 'Nenhum horário selecionado';

  const first = payloads[0];
  let dateLabel = first.starts_on;
  if (first.block_type === 'recurrent') {
    dateLabel = weekdayLabel(first.weekday);
  } else if (first.starts_on !== first.ends_on) {
    dateLabel = `${first.starts_on} até ${first.ends_on}`;
  }

  const times = payloads
    .map(payload => `${payload.start_time}-${payload.end_time}`)
    .join(', ');

  return `${dateLabel} · ${times}`;
};
